import { useState, useEffect, useCallback } from 'react';
import chatService, { ChatMessage, ChatPreferences } from '../services/chatService';
import userService from '../services/userService';

interface UseChatMessagesReturn {
    messages: ChatMessage[];
    isLoading: boolean;
    error: string | null;
    sendMessage: (message: string) => Promise<void>;
    loadMoreMessages: () => Promise<boolean>;
    preferences: ChatPreferences | null;
    updatePreferences: (prefs: Partial<ChatPreferences>) => Promise<void>;
    setUserStatus: (status: 'online' | 'away' | 'busy') => Promise<void>;
}

export function useChatMessages(walletAddress: string, walletProvider: string, userName: string, avatarUrl: string): UseChatMessagesReturn {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [preferences, setPreferences] = useState<ChatPreferences | null>(null);
    const [oldestTimestamp, setOldestTimestamp] = useState<string | undefined>(undefined);
    const [oldestSequenceId, setOldestSequenceId] = useState<number | undefined>(undefined);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [userId, setUserId] = useState<string | null>(null);

    // Function to get or create user ID from wallet address
    const getUserId = useCallback(async () => {
        try {
            // Try to get existing user first
            let user = await userService.getUserByWallet(walletAddress);

            // If no user found, create one
            if (!user) {
                user = await userService.createOrUpdateUser(walletAddress, walletProvider || 'unknown');
            }

            if (user && user.id) {
                setUserId(user.id);
                return user.id;
            } else {
                console.error('Failed to get or create user for wallet:', walletAddress);
                setError('Failed to get or create user account');
                return null;
            }
        } catch (err) {
            console.error('Error getting user ID:', err);
            setError('Error getting user account');
            return null;
        }
    }, [walletAddress, walletProvider]);

    // Function to load initial messages
    const loadInitialMessages = useCallback(async () => {
        try {
            setIsLoading(true);
            const history = await chatService.getChatHistory();

            if (history.length > 0) {
                setMessages(history);
                // Store both timestamp and sequence_id for backward compatibility
                setOldestTimestamp(history[0]?.timestamp);
                setOldestSequenceId(history[0]?.sequence_id);
                setHasMoreMessages(history.length >= 50);
            }

            setIsLoading(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error loading chat history');
            setIsLoading(false);
        }
    }, []);

    // Function to load more messages (pagination)
    const loadMoreMessages = useCallback(async () => {
        if (!hasMoreMessages || (!oldestTimestamp && !oldestSequenceId)) return false;

        try {
            setIsLoading(true);
            // Prefer sequence_id for pagination if available (more reliable)
            const olderMessages = oldestSequenceId
                ? await chatService.getChatHistory(50, undefined, oldestSequenceId)
                : await chatService.getChatHistory(50, oldestTimestamp);

            if (olderMessages.length > 0) {
                setMessages(prev => [...olderMessages, ...prev]);
                // Update pagination references
                setOldestTimestamp(olderMessages[0]?.timestamp);
                setOldestSequenceId(olderMessages[0]?.sequence_id);
                setHasMoreMessages(olderMessages.length >= 50);
            } else {
                setHasMoreMessages(false);
            }

            setIsLoading(false);
            return olderMessages.length > 0;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error loading more messages');
            setIsLoading(false);
            return false;
        }
    }, [hasMoreMessages, oldestTimestamp, oldestSequenceId]);

    // Function to load user preferences
    const loadPreferences = useCallback(async (id: string) => {
        try {
            const prefs = await chatService.getChatPreferences(id);
            if (prefs) {
                setPreferences(prefs);
            }
        } catch (err) {
            console.error('Error loading chat preferences:', err);
        }
    }, []);

    // Function to send a message
    const sendMessage = useCallback(async (message: string) => {
        try {
            if (!message.trim() || !userId) return;

            // Use the preferences color if available, otherwise default
            const userColor = preferences?.user_color || '#42ae84';

            const newMessage = await chatService.sendMessage(
                userId,
                walletAddress,
                message,
                userName,
                avatarUrl,
                userColor
            );

            if (newMessage) {
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error sending message');
        }
    }, [userId, walletAddress, userName, avatarUrl, preferences]);

    // Function to update preferences
    const updatePreferences = useCallback(async (prefs: Partial<ChatPreferences>) => {
        try {
            if (!preferences || !userId) return;

            const updatedPrefs = await chatService.saveChatPreferences({
                ...preferences,
                ...prefs,
                user_id: userId
            });

            if (updatedPrefs) {
                setPreferences(updatedPrefs);
            }
        } catch (err) {
            console.error('Error updating preferences:', err);
        }
    }, [preferences, userId]);

    // Function to update user status
    const setUserStatus = useCallback(async (status: 'online' | 'away' | 'busy') => {
        try {
            if (!userId) return;

            const success = await chatService.updateUserStatus(userId, status);

            if (success && preferences) {
                setPreferences({
                    ...preferences,
                    user_status: status
                });
            }
        } catch (err) {
            console.error('Error updating user status:', err);
        }
    }, [userId, preferences]);

    // Initialize chat service and load initial data
    useEffect(() => {
        if (!walletAddress) return;

        // Initialize chat service
        const initChat = async () => {
            await chatService.init();
            const id = await getUserId();
            if (id) {
                await loadInitialMessages();
                await loadPreferences(id);
            }
        };

        initChat();

        // Subscribe to new messages
        const unsubscribe = chatService.subscribe((newMessage) => {
            setMessages(prev => [...prev, newMessage]);
        });

        // Cleanup on unmount
        return () => {
            unsubscribe();
            chatService.closeSubscription();
        };
    }, [walletAddress, getUserId, loadInitialMessages, loadPreferences]);

    return {
        messages,
        isLoading,
        error,
        sendMessage,
        loadMoreMessages,
        preferences,
        updatePreferences,
        setUserStatus
    };
} 